/**
 * Le routeur du back-office — **dérivé** de la table des modules.
 *
 * Les seize routes ne sont pas écrites une à une : elles sont engendrées
 * depuis `backOfficeModules`. Ajouter un module se fait à un seul endroit, et
 * un module retiré de la table disparaît du routeur **et** de la navigation du
 * même geste.
 *
 * **La connexion est en dehors de la coquille**, et c'est la seule route qui y
 * soit : elle n'a ni barre latérale, ni barre supérieure, ni module courant.
 * Tout le reste — les seize modules, la racine et l'écran d'erreur de route —
 * passe par `RequireSession`, qui ramène à `B-01` tant qu'aucune session n'est
 * ouverte. Cette garde est du confort, pas une protection : voir
 * `session-guard.tsx`.
 *
 * Ce composant ne choisit pas son routeur : `main.tsx` pose un
 * `BrowserRouter`, une suite de tests pose un `MemoryRouter`. Un routeur figé
 * ici rendrait chaque test tributaire de l'historique du navigateur.
 */

import type { ReactNode } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './AppShell.js';
import { LoginScreen } from './LoginScreen.js';
import { PlaceholderScreen } from './PlaceholderScreen.js';
import { RouteErrorScreen } from './RouteErrorScreen.js';
import { RequireSession } from './session-guard.js';
import { AuditLogScreen } from './AuditLogScreen.js';
import { ReportingScreen } from './ReportingScreen.js';
import { OutletNetworkScreen } from './OutletNetworkScreen.js';
import { PricingScreen } from './PricingScreen.js';
import { SystemSettingsScreen } from './SystemSettingsScreen.js';
import { UsersRolesScreen } from './UsersRolesScreen.js';
import { SupportScreen } from './SupportScreen.js';
import { ComplianceScreen } from './ComplianceScreen.js';
import { CustomersScreen } from './CustomersScreen.js';
import { DashboardScreen } from './DashboardScreen.js';
import { FinanceScreen } from './FinanceScreen.js';
import { IdentityReviewScreen } from './IdentityReviewScreen.js';
import { TransactionsScreen } from './TransactionsScreen.js';
import { TontinesScreen } from './TontinesScreen.js';
import { TransactionDetailScreen } from './TransactionDetailScreen.js';
import { PartyScreen } from './PartyScreen.js';
import {
  backOfficeModules,
  hasDeliveredScreen,
  homeModulePath,
  loginPath,
} from './routes.js';

/**
 * L'écran métier de chaque module livré.
 *
 * Cette table et `deliveredModuleIds` disent la même chose de deux endroits —
 * la première ne peut pas vivre dans `routes.ts`, qui ne connaît pas React.
 * `routing.test.tsx` exige donc qu'elles portent **exactement** les mêmes
 * identifiants : sans ce contrôle, un module déclaré livré et sans écran
 * rendrait `undefined`, c'est-à-dire une page blanche, et un écran écrit mais
 * jamais déclaré ne serait atteignable par personne.
 */
export const deliveredScreens: Readonly<Record<string, ReactNode>> = {
  identityReview: <IdentityReviewScreen />,
  auditLog: <AuditLogScreen />,
  dashboard: <DashboardScreen />,
  customers: <CustomersScreen />,
  finance: <FinanceScreen />,
  compliance: <ComplianceScreen />,
  transactions: <TransactionsScreen />,
  reporting: <ReportingScreen />,
  support: <SupportScreen />,
  outletNetwork: <OutletNetworkScreen />,
  pricing: <PricingScreen />,
  usersRoles: <UsersRolesScreen />,
  systemSettings: <SystemSettingsScreen />,
  communityProducts: <TontinesScreen />,
};

/** Les routes de l'application. */
export function App(): ReactNode {
  return (
    <Routes>
      <Route element={<LoginScreen />} path={loginPath} />
      <Route element={<RequireSession />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate replace to={homeModulePath} />} />
          {backOfficeModules.map((module) => (
            <Route
              element={
                hasDeliveredScreen(module) ? deliveredScreens[module.id] : <PlaceholderScreen />
              }
              key={module.id}
              path={module.path}
            />
          ))}
          {/* **Les deux écrans que le module TRANSACTIONS ouvre sous lui.**

              Ils ne sont pas dans la table des modules, et ils n'ont pas à y
              être : la table dit les seize DESTINATIONS de la barre latérale,
              pas les écrans qu'une destination ouvre. Les y ajouter aurait mis
              deux entrées de menu vers des écrans qui n'ont de sens que sur une
              opération ou un compte désignés.

              Leurs chemins vivent SOUS `/transactions`, et c'est ce qui garde
              la barre latérale et la barre supérieure justes : `moduleOfPath`
              résout un chemin par préfixe, le module courant reste
              « Transactions », et le lien du menu reste actif.

              `/transactions/accounts/:accountId` est déclaré AVANT le chemin
              variable dans l'ordre de lecture ; React Router, lui, classe par
              spécificité — un segment fixe l'emporte sur un segment variable —,
              et « accounts » ne sera donc jamais lu comme un identifiant
              d'opération. */}
          <Route element={<PartyScreen />} path="/transactions/accounts/:accountId" />
          <Route element={<TransactionDetailScreen />} path="/transactions/:transactionId" />
          <Route element={<RouteErrorScreen />} path="*" />
        </Route>
      </Route>
    </Routes>
  );
}
